import { useEffect } from 'react';
import { QueryKey, useQuery } from '@tanstack/react-query';

import { useApiClient } from 'app/data/api-client';
import { GetUiMetadataQueryResponse, UiMetadata, UiMetadataParams } from 'app/data/types/ui-metadata';
import { ERROR_DEFAULT } from 'app/shared/consts/error-codes';
import { OwnerType, Verticals } from 'app/shared/consts/enums';
import { useApi } from 'app/shared/contexts/api';
import { useErrors } from 'app/shared/contexts/errors';
import { useOpenParams } from 'app/shared/hooks/use-open-params/use-open-params.hook';
import { retryOnAuthError } from 'app/shared/auth';

export const UI_METADATA_QUERY_KEY = 'ui-metadata';

export const getUseUiMetadataQueryKey = (ownerType: string, ownerId: string): QueryKey => [
  UI_METADATA_QUERY_KEY,
  ownerType,
  ownerId,
];

export const useUiMetadataQuery = (params?: UiMetadataParams): GetUiMetadataQueryResponse => {
  const client = useApiClient();
  const { apiUrl } = useApi();
  // Terminal channel — ui-metadata feeds MiniappSdkProvider's `app_uuid`.
  // Without it the SDK never initializes and the app can't function, so
  // a failure here should route to /error like the other foundational
  // queries (building / user / theme), not pop a dismissible modal.
  const { addGenericError, hideGenericErrors } = useErrors();
  const { userBuildingUuid } = useOpenParams();

  const ownerType = params?.ownerType || OwnerType.BUILDING;
  const ownerId = params?.ownerId || userBuildingUuid;

  const { data, error, isLoading, isSuccess } = useQuery<UiMetadata | null, Error>({
    queryKey: getUseUiMetadataQueryKey(ownerType, ownerId),
    queryFn: async () => {
      const result = await client.get<UiMetadata>(
        `/api/verticals/${Verticals.RESOURCE_BOOKING}/${ownerType.toUpperCase()}/${ownerId}/ui-metadata`,
      );
      return result ?? null;
    },
    enabled: !!ownerId && !!apiUrl,
    retry: retryOnAuthError,
  });

  useEffect(() => {
    if (error) {
      addGenericError(UI_METADATA_QUERY_KEY, { error, errorCode: ERROR_DEFAULT });
    } else {
      hideGenericErrors(UI_METADATA_QUERY_KEY);
    }
  }, [error, addGenericError, hideGenericErrors]);

  return {
    uiMetadata: data ?? null,
    error: error ? { error, errorCode: ERROR_DEFAULT } : null,
    isLoading,
    isSuccess,
  };
};
